import { type LucideIcon, Palmtree, Shapes, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ASSETS } from '@swim/config'
import { LocaleLink } from '@/components/LocaleLink'
import { parseLocalePath } from '@/lib/router'
import { swimApi } from '@swim/api-client'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { useSession } from '@/hooks/use-session'

/**
 * SideNav — Hover-expand sidebar (shadcn Sidebar aesthetic)
 *
 * Collapsed (default): 40px icon rail, no background
 * Expanded (hover):    192px panel with labels, frosted sand / dark glass
 *
 * NOTE: Installing shadcn `<Sidebar>` proper would require wrapping the entire Layout in
 * `<SidebarProvider>` and restructuring the grid. This component achieves the same visual
 * result without touching the layout shell.
 */

interface NavItem {
  key: string
  icon?: string
  LucideIcon?: LucideIcon
  label: string
  route: string
  external?: boolean
  hidden?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { key: 'home',       LucideIcon: Palmtree, label: 'Latest Offers',  route: '/' },
  { key: 'streams',    icon: ASSETS.nav.streams, label: 'Streams',     route: '/streams', hidden: true },
  { key: 'categories', LucideIcon: Shapes,   label: 'Categories',      route: '/categories' },
  // Same destination as the AvatarMenu "My Claimed Offers" entry — Tag icon matches it
  { key: 'claimed',    LucideIcon: Tag,      label: 'My Claimed Offers', route: '/account/claimed-offers' },
  // Hidden until sections are ready — keep order for when we unhide
  { key: 'currents', icon: ASSETS.nav.currents, label: 'Currents', route: '/browse',  hidden: true },
  { key: 'drops',    icon: ASSETS.nav.splash,   label: 'Drops',    route: '/drops',   hidden: true },
]

const DOCS = { key: 'docs', label: 'Docs', route: (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_DOCS_URL ?? 'https://docs.swimflows.com' }

function NavRow({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`flex items-center gap-3 w-full h-10 px-2 rounded-xl transition-all duration-200 ${className}`}>
      {children}
    </div>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-sm font-medium font-sans whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 delay-75 text-slate-600 dark:text-sand-300 overflow-hidden">
      {children}
    </span>
  )
}

const PREFETCH_FNS: Partial<Record<string, () => Promise<unknown>>> = {
  '/categories': async () => {
    const result = await swimApi.get<{ categories: unknown[] }>('/categories')
    if (!result.success) throw new Error(result.error)
    return result.data.categories
  },
}

export function SideNav() {
  const { t } = useTranslation('navigation')
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const { user } = useSession()

  // Strip locale prefix before checking active state — pathname is /us/en/streams, route is /streams
  const rest = parseLocalePath(pathname)?.rest ?? pathname
  const isActive = (route: string) =>
    route === '/' ? rest === '/' : rest.startsWith(route)

  function handlePrefetch(route: string) {
    const fn = PREFETCH_FNS[route]
    if (!fn) return
    const queryKey = route === '/categories' ? QUERY_KEYS.categories.list() : null
    if (!queryKey) return
    queryClient.prefetchQuery({ queryKey, queryFn: fn, staleTime: 5 * 60 * 1000 })
  }

  // Claimed Offers only when signed in — global rule (Mars 2026-07-05)
  const visibleItems = NAV_ITEMS.filter((item) => !item.hidden && (item.key !== 'claimed' || !!user))

  return (
    <nav
      aria-label={t('sidenav.ariaLabel')}
      className={[
        'group hidden md:flex flex-col items-start gap-1',
        // Collapsed: 40px; Expanded: 192px. focus-within matches hover so a
        // Tab-focused nav item's focus ring isn't clipped by overflow-clip
        // while the rail is still collapsed — :hover never fires on
        // keyboard focus, so without this the ring gets cut at 40px.
        'w-10 hover:w-48 focus-within:w-48 transition-all duration-300 ease-in-out overflow-clip',
        // No background at rest; frosted panel on hover or keyboard focus
        'hover:bg-sand-300/70 dark:hover:bg-slate-800/80',
        'focus-within:bg-sand-300/70 dark:focus-within:bg-slate-800/80',
        'hover:rounded-2xl hover:shadow-sm hover:backdrop-blur-sm',
        'focus-within:rounded-2xl focus-within:shadow-sm focus-within:backdrop-blur-sm',
      ].join(' ')}
    >
      {/* ── Nav items ────────────────────────────────────────────────── */}
      {visibleItems.map((item) => {
        const active = isActive(item.route)

        const rowClass = active
          ? 'bg-sand-400/60 dark:bg-sand-200/10'
          : 'hover:bg-sand-400/40 dark:hover:bg-sand-200/10'

        const iconColor = active
          ? 'text-slate-800 dark:text-sand-100'
          : 'text-slate-400 dark:text-slate-300 group-hover:text-slate-600 dark:group-hover:text-sand-100 group-focus-within:text-slate-600 dark:group-focus-within:text-sand-100'

        const labelColor = active
          ? '!text-slate-800 dark:!text-sand-100'
          : ''

        const icon = item.LucideIcon ? (
          <item.LucideIcon className={`w-6 h-6 flex-shrink-0 transition-colors duration-200 ${iconColor}`} />
        ) : item.icon ? (
          <img
            src={item.icon}
            alt={item.label}
            className="w-6 h-6 flex-shrink-0 dark:invert"
          />
        ) : null

        if (item.external) {
          return (
            <a
              key={item.key}
              href={item.route}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.label}
            >
              <NavRow className={rowClass}>
                {icon}
                <Label>{item.label}</Label>
              </NavRow>
            </a>
          )
        }

        return (
          <LocaleLink
            key={item.key}
            to={item.route}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className="w-full"
            onMouseEnter={() => handlePrefetch(item.route)}
          >
            <NavRow className={rowClass}>
              {icon}
              <Label>
                <span className={labelColor}>{item.label}</span>
              </Label>
            </NavRow>
          </LocaleLink>
        )
      })}

      {/* ── Docs (? glyph) ───────────────────────────────────────────── */}
      <a
        href={DOCS.route}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={DOCS.label}
        className="w-full"
      >
        <NavRow className="hover:bg-sand-400/40 dark:hover:bg-sand-200/10">
          <span className={[
            'w-6 h-6 flex items-center justify-center flex-shrink-0',
            'text-xl font-semibold font-sans leading-none select-none',
            'text-slate-400 dark:text-slate-300',
            'group-hover:text-slate-600 dark:group-hover:text-sand-100',
            'group-focus-within:text-slate-600 dark:group-focus-within:text-sand-100',
            'transition-colors duration-200',
          ].join(' ')}>
            ?
          </span>
          <Label>{DOCS.label}</Label>
        </NavRow>
      </a>

      {/* Theme toggle lives in LogoMenu (top-left SWIM logo flyout) */}
    </nav>
  )
}
